import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import Paper from "@mui/material/Paper";
import StyledTablePagination from "../TablePagination/TablePagination.tsx";
import { TableTitles } from "../interfaces/TitulosTablas.ts";

//REQUERIDOS PARA EL BOTON DE COPIAR AL PORTAPAPELES
import { Bounce, toast } from "react-toastify";
import { getOcultarColumn } from "../helper/tableValidation.ts";
import SkeletonTabla from "../SklTable/SklTable.tsx";
import { clearSpecificSessionStorage } from "../helper/filtrarLista.ts";
import SklListJustSearch from "../SklListJustSearch/SklListJustSearch.tsx";

export interface FetchParamsInterface {
  page: number;
  size: number;
  filters: { [key: string]: string[] };
  textFilter: string;
}

interface TableInterface {
  children?: Function; //Se envia como hijo la Fila a Mostrar
  titles?: TableTitles[]; //Array con los titulos de la tabla
  size?: "small" | "medium";
  containerClassName?: string; //ClassName para el contenedor de las listas en caso de que no sea de tipo tabla
  searchName?: string; //nombre con el que se va a vincular el input con la tabla
  filtersToApply?: string[]; //array con los identificadores para aplicar los filtros
  multiplicador?: number; //numero inicial pora la cantidad de registros por paginas
  rowsPerPageOptions?: number[]; //Array para las opciones de numero por pagina
  dataFetch: Function; //Funcion con el fetch para llamar a la data desde la base de datos. Esta funcion debe aceptar los parametros establecidos en FetchParamsInterface
  isList?: boolean;
  params?: any; //Para enviar datos a la funcion en caso de que se deseen aplicar filtros específicos
  Skeleton?: ReactNode; //Skeleteon customizado para listas especificas. Si no se envia se deja el skeleteon por defecto.
  addEmptyComponent?: Function; //Para mostrar una empty card para agregar uno nuevo. Se debe enviar el componente como tal.
}

export interface TableCustomeHandleRef {
  refreshData(): void;
}

export type TableCustomeRef = {
  refreshData(): void;
};

export interface ItemsF {
  name: string;
  value: string | string[];
  hide?: "row" | "col" | "none";
}

// Tipo para el resultado deseado, donde `row` es un objeto y `col` una lista de strings
interface ResultadoSeparado {
  row: { [key: string]: string | string[] };
  col: string[];
}

/**
 * ******************************************************************************
 *                                 MAIN COMPONENT
 * ******************************************************************************
 *
 * Tabla empleada para mostrar datos que retornen un objeto de tipo paginacion. Ver la seccion de documentacion TableCustomeHandle para comprender los diferentes objetos que envia y recibe.
 * La tabla recbe una funcion de tipo fetch la cual internamente llamara al instanciarse y tambien al moverse entre las paginas.
 * Internamente gestiona los eventos de filtros aplicados. Como son los filtros específicos de componentes de filtros, y tambien filtro segun campos del serch bar.
 *
 * @param {TableInterface} param
 * @returns
 */

const TableCustomeHandle = forwardRef<TableCustomeHandleRef, TableInterface>(
  (
    {
      children,
      size = "medium",
      titles,
      containerClassName,
      dataFetch,
      isList = false,
      searchName,
      filtersToApply,
      multiplicador,
      rowsPerPageOptions,
      params,
      Skeleton,
      addEmptyComponent,
    }: TableInterface,
    ref
  ) => {
    const [colFilters, setColFilters] = useState<string[]>();

    const [colFiltrosAplicados, setColFiltrosAplicados] = useState(false);

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(25);
    const [optionsPerPage, setOptionsPerPage] = useState<number[]>([
      25, 50, 100,
    ]);

    const [totalRegistros, setTotalRegistros] = useState<number>(0);

    const [tableData, settableData] = useState<any>([]);
    const [needsSearch, setNeedsSearch] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      refreshData() {
        fetchData();
      },
    }));

    const handleChangePage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    };

    const fetchData = async (tempPage?: number) => {
      try {
        setLoading(true);
        const reducedObject = filtersToApply?.reduce(
          (acc: ResultadoSeparado, filterName: string, index: number) => {
            const filterResult = buscarFiltrosAlmacenadosSS(
              `pag_filter${filterName}`
            );
            acc.row = { ...acc.row, ...filterResult.row };
            acc.col = [...acc.col, ...filterResult.col];
            return acc;
          },
          { row: {}, col: [] } as ResultadoSeparado
        );

        if (reducedObject?.col) {
          setColFilters(reducedObject?.col); //se almacenan los fitros de las columns
        }
        let SSFilterText: any = sessionStorage.getItem(
          `pag_table_searchTxt${searchName}`
        );
        let searchInputTemp: string = "";
        if (SSFilterText) {
          searchInputTemp = JSON.parse(
            sessionStorage.getItem(`pag_table_searchTxt${searchName}`) || ""
          );
        }
        let filtersForFetch = reducedObject?.row
          ? { ...reducedObject?.row }
          : {};
        if (params?.filters) {
          filtersForFetch = {
            ...filtersForFetch,
            ...params?.filters,
          };
        }
        await dataFetch({
          page: tempPage || page + 1,
          size: rowsPerPage,
          filters: filtersForFetch,
          textFilter: searchInputTemp,
          params: params,
        })
          .then((resp: any) => {
            if (resp) {
              settableData(resp?.data || []);
              setTotalRegistros(resp?.total_records);
            } else {
              settableData([]);
              setTotalRegistros(0);
            }
          })
          .finally(() => {
            setNeedsSearch(false);
            setLoading(false);
          });
      } catch (error) {
        console.error("Error fetching the data in table...", error);
        settableData([]);
        setTotalRegistros(0);
        setNeedsSearch(false);
      }
    };

    const buscarFiltrosAlmacenadosSS = (filterName: string): any => {
      if (
        sessionStorage.getItem(`${filterName}`) &&
        sessionStorage.getItem(`${filterName}`) !== null
      ) {
        let filterTEmp: ItemsF[] = JSON.parse(
          sessionStorage.getItem(`${filterName}`) || ""
        );

        const resultado = filterTEmp.reduce<ResultadoSeparado>(
          (acc, { name, value, hide }) => {
            if (hide === "row") {
              acc.row[name] = value as string;
            } else if (hide === "col") {
              acc.col.push(name); // Aquí simplemente acumulamos los nombres de los elementos con hide="col"
            }
            return acc;
          },
          { row: {}, col: [] }
        );
        return resultado;
      } else {
        return {
          col: [],
          row: {},
        };
      }
    };

    useEffect(() => {
      setNeedsSearch(true);
    }, [page, rowsPerPage]);

    useEffect(() => {
      if (needsSearch) {
        fetchData();
      }
    }, [needsSearch]);

    useEffect(() => {
      clearSpecificSessionStorage();

      const handlePagStorage = () => {
        if (page === 0) {
          fetchData(0);
        } else {
          setPage(0);
        }
      };

      const handlePagFilter = () => {
        if (page === 0) {
          fetchData(0);
        } else {
          setPage(0);
        }
      };

      window.addEventListener(`pag_storage${searchName}`, handlePagStorage);

      filtersToApply?.forEach((filterName: string) => {
        window.addEventListener(`pag_filter${filterName}`, handlePagFilter);
      });

      return () => {
        window.removeEventListener(
          `pag_storage${searchName}`,
          handlePagStorage
        );
        filtersToApply?.forEach((filterName: string) => {
          window.removeEventListener(
            `pag_filter${filterName}`,
            handlePagFilter
          );
        });
      };
    }, []);

    useEffect(() => {
      if (!colFilters || (colFilters && colFilters?.length <= 0)) {
        setColFiltrosAplicados(false);
      } else {
        setColFiltrosAplicados(true);
      }
    }, [colFilters]);

    useEffect(() => {
      if (
        multiplicador &&
        multiplicador !== 0 &&
        rowsPerPageOptions &&
        rowsPerPageOptions?.length > 0
      ) {
        setRowsPerPage(multiplicador);
        if (!rowsPerPageOptions?.includes(multiplicador)) {
          setOptionsPerPage(
            [multiplicador, ...rowsPerPageOptions].slice().sort((a, b) => a - b)
          );
        } else {
          setOptionsPerPage(rowsPerPageOptions);
        }
      }
    }, [multiplicador, rowsPerPageOptions]);

    if (loading) {
      if (Skeleton) {
        return Skeleton;
      } else if (isList) {
        return <SklListJustSearch />;
      } else {
        return <SkeletonTabla />;
      }
    } else {
      if (tableData?.length > 0 || addEmptyComponent) {
        return (
          <>
            {isList && children && (
              <div className={containerClassName || "flex flex-col gap-y-1"}>
                {typeof addEmptyComponent === "function" && addEmptyComponent()}
                {tableData &&
                  tableData?.length > 0 &&
                  tableData?.map((row: any, index: number) =>
                    children(row, index)
                  )}
              </div>
            )}
            {!isList && (
              <TableContainer component={Paper} className="!flex-1">
                <Table stickyHeader aria-label="collapsible table">
                  <TableHead>
                    <TableRow>
                      {titles?.map((item: any, index: number) => {
                        if (
                          !colFiltrosAplicados ||
                          (colFiltrosAplicados &&
                            !getOcultarColumn(
                              item?.key,
                              colFiltrosAplicados,
                              colFilters
                            ))
                        ) {
                          return (
                            <TableCell
                              key={`Cell-${index}`}
                              style={
                                item?.sticky && item?.sticky === true
                                  ? {
                                      position: "sticky",
                                      left: item?.position ? item?.position : 0,
                                      zIndex: 3,
                                      backgroundColor: "white",
                                      fontWeight: "bold",
                                    }
                                  : {}
                              }
                              className={`!py-1 !px-[10px] ${item?.className} `}
                              align={item?.align}
                              sx={{ textTransform: "uppercase" }}
                              title={item?.label}
                            >
                              <div
                                className="whitespace-pre-line leading-tight text-[10px]"
                                title={item?.label}
                              >
                                {item?.label}
                              </div>
                            </TableCell>
                          );
                        }
                        return "";
                      })}
                    </TableRow>
                  </TableHead>
                  {children ? (
                    <TableBody>
                      {tableData &&
                        tableData?.length > 0 &&
                        tableData?.map((row: any, index: number) =>
                          children(row, index)
                        )}
                    </TableBody>
                  ) : (
                    <TableBody>
                      {tableData &&
                        tableData?.length > 0 &&
                        tableData?.map((row: any, index: number) => (
                          <TableRow key={`row${index}`}>
                            {titles?.map((item: any, index: number) => {
                              if (
                                !colFiltrosAplicados ||
                                (colFiltrosAplicados &&
                                  !getOcultarColumn(
                                    item?.key,
                                    colFiltrosAplicados,
                                    colFilters
                                  ))
                              ) {
                                return (
                                  <td
                                    title={row?.[item?.key]}
                                    key={index}
                                    className={`border-b py-1 px-[10px] text-[12px] justify-center  ${
                                      item?.contentClassName || item?.className
                                    } `}
                                  >
                                    <div
                                      title={row?.[item?.key]}
                                      className={`flex ${
                                        item?.copyPosition === "bottom"
                                          ? "flex-col"
                                          : "flex-row"
                                      }`}
                                      style={{ justifyContent: "inherit" }}
                                    >
                                      {item?.enableCopy &&
                                        item?.copyPosition === "left" && (
                                          <CopyToclipboard
                                            text={row?.[item?.key]}
                                          />
                                        )}
                                      <span className="line-clamp-1">
                                        {row?.[item?.key]}
                                      </span>
                                      {item?.enableCopy &&
                                        (item?.copyPosition === "right" ||
                                          item?.copyPosition === "bottom" ||
                                          !item?.copyPosition) && (
                                          <CopyToclipboard
                                            text={row?.[item?.key]}
                                          />
                                        )}
                                    </div>
                                  </td>
                                );
                              }
                              return "";
                            })}
                          </TableRow>
                        ))}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            )}
            {totalRegistros > 0 && (
              <StyledTablePagination
                size={size}
                rowsPerPageOptions={optionsPerPage}
                count={totalRegistros}
                rowsPerPage={rowsPerPage}
                page={page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            )}
          </>
        );
      } else {
        return (
          <div className="flex flex-col items-center justify-center gap-y-1 py-6 min-h-[200px] text-brand-grey">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1"
              stroke="currentColor"
              className="w-20 h-20"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"
              />
            </svg>

            <p className="font-nunito text-lg font-semibold text-center">
              There are no registers
            </p>
          </div>
        );
      }
    }
  }
);

export default TableCustomeHandle;

/**
 * ******************************************************************************
 *                                 CUSTOM IMPORTABLE COMPONENT
 * ******************************************************************************
 *
 * Componente para crer un input de tipo busqueda. Funciona aislado de la tabla.
 * Funcionamiento: Cada vez que se cambia el texto en el input se almacena el valor en local storage y se genera un evento.
 * Resultado: Desde el lugar o componente que se desee se debe poner un addEventListener y dentro de la funcion lee la variable almacenada en local storage
 *
 * Evento Disparado: storage
 * Variable Local Storage:
 *
 * @param {string} param.placeholder
 * @returns
 */
export function SearchInputHandle({
  placeholder = "Search ...",
  eventName = "",
}: {
  placeholder?: string;
  eventName?: string;
}) {
  const [searchText, setSearchText] = useState("");

  const onhandleChange = (e: any) => {
    setSearchText(e.target.value);
  };

  const sendSearchEvent = (e: any) => {
    e.preventDefault();
    sessionStorage.setItem(
      `pag_table_searchTxt${eventName}`,
      JSON.stringify(searchText)
    );
    window.dispatchEvent(new Event(`pag_storage${eventName}`));
  };

  useEffect(() => {
    if (searchText === "") {
      sessionStorage.setItem(
        `pag_table_searchTxt${eventName}`,
        JSON.stringify(searchText)
      );
      window.dispatchEvent(new Event(`pag_storage${eventName}`));
    }
  }, [searchText]);

  return (
    <form
      id="input_handle_search"
      className="flex-1 max-w-md min-w-[200px] flex md:min-w-[250px]"
      onSubmit={sendSearchEvent}
    >
      <div className="relative w-full flex flex-row">
        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-500 dark:text-gray-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 20"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
            />
          </svg>
        </div>
        <input
          value={searchText}
          onChange={onhandleChange}
          type="search"
          id="default-search"
          autoComplete="off"
          className="rounded-lg flex-1 shadow block w-full p-2 ps-10 pe-20 text-base text-gray-900 border border-gray-400 bg-white focus:ring-blue-500 focus:border-blue-500 "
          placeholder={placeholder}
        />
        {searchText && searchText !== "" && (
          <button
            type="submit"
            className="rounded-e-lg leading-tight text-white absolute right-0  rounded-sm bg-brand-dark hover:bg-brand-primary hover:text-brand-dark focus:ring-1 focus:outline-none focus:ring-blue-300 font-bold text-sm px-4 py-1 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 h-full"
          >
            Buscar
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * ******************************************************************************
 *                                 CUSTOM  COMPONENT
 * ******************************************************************************
 *
 * Componente para copiar texto al portapapeles.
 *
 * @param children Contenido del botón.
 * @param text Texto a copiar al portapapeles.
 * @param title Título del botón (opcional).
 * @param position Posición del icono en relación al texto ("left" o "right", por defecto es "left").
 */

interface ButtonInterface {
  children?: ReactNode | string; //texto o componente a mostrar al lado del icono para copiar
  text: string; //retorna el objeto ya ordenado y agregado a las otras listas
  title?: string; //utilizado para mostar el texto al poner el mouse sobre el raton.
  position?: "left" | "right"; //posicion en caso de que exista texto o algun componente adicional
}

function CopyToclipboard({
  children,
  text,
  title,
  position = "left",
}: ButtonInterface) {
  const [copied, setCopied] = useState(false);

  const copyToClipboardText = (e: any) => {
    e?.preventDefault();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        toast.success("Texto copiadio!", {
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setTimeout(() => setCopied(false), 3000);
      })
      .catch((err) => {
        setCopied(false);
        console.error("Something went wrong", err);
      });
  };
  return (
    <button
      className="btn-icon flex flex-col items-center leading-tight text-[9px] text-gray-500 font-light py-[2px] px-[2px] rounded-full hover:text-blue-800"
      onClick={copyToClipboardText}
      title={title || "Copiar al portapapeles"}
      style={{ color: copied ? "green" : "gray" }}
      disabled={copied}
    >
      {position === "left" && children}
      {copied ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-[15px] h-[15px] text-gray-300"
        >
          <path d="M16.5 6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v7.5a3 3 0 0 0 3 3v-6A4.5 4.5 0 0 1 10.5 6h6Z" />
          <path d="M18 7.5a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3h-7.5a3 3 0 0 1-3-3v-7.5a3 3 0 0 1 3-3H18Z" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1"
          stroke="currentColor"
          className={`w-[15px] h-[15px] text-gray-600`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 8.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v8.25A2.25 2.25 0 0 0 6 16.5h2.25m8.25-8.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-7.5A2.25 2.25 0 0 1 8.25 18v-1.5m8.25-8.25h-6a2.25 2.25 0 0 0-2.25 2.25v6"
          />
        </svg>
      )}
      {position === "right" && children}
    </button>
  );
}
